import cn from 'classnames';
import React from 'react';
import {useSelector} from 'react-redux';
import {Route} from 'react-router-dom';

import appConfig from '../../config/appConfig';
import domClassNames from '../../consts/app/domClassNames';
import useFwuBackgroundCheck from '../../hooks/fwu/useFwuBackgroundCheck';
import useDirection from '../../hooks/useDirection';
import useDocumentDescription from '../../hooks/useDocumentDescription';
import useHistoryScrollTop from '../../hooks/useHistoryScrollTop';
import useLayoutChange from '../../hooks/useLayoutChange';
import useListenBluetoothAvailabilityChange from '../../hooks/useListenBluetoothAvailabilityChange';
import userDeviceService from '../../services/user/userDeviceService';
import {makeSelectIsTncPopupVisible} from '../../state/selectors/tnc';
import Accessibility from '../Accessibility/Accessibility';
import LoaderGlobal from '../LoaderGlobal/LoaderGlobal';
import ModalsRoot from '../Modals/ModalsRoot';
import Notifications from '../Notifications/Notifications';
import YapPairingChecker from '../ServiceContainers/YapPairingChecker/YapPairingChecker';
import YapRequestStatusChecker from '../ServiceContainers/YapRequestStatusChecker/YapRequestStatusChecker';
import TermsOfUsePopup from '../TNC/TermsOfUsePopup/TermsOfUsePopup';
import AppFooter from './components/AppFooter';
import AppHeader from './components/AppHeader';
import DataCollectionHandler from './components/DataCollectionHandler';
import DeviceConnectionChecker from './components/DeviceConnectionChecker';
import DeviceHolderConnectionChecker from './components/DeviceHolderConnectionChecker';
import OfflineDetector from './components/OfflineDetector';
import YapOfflineDetector from './components/YapOfflineDetector';
import useDeviceConnectionNotification from './hooks/useDeviceConnectionNotification';
import useDeviceIdlePingSchedulerPause from './hooks/useDeviceIdlePingSchedulerPause';
import useHideReconnectionGuideModal from './hooks/useHideReconnectionGuideModal';
import useIotDeviceConfigReset from './hooks/useIotDeviceConfigReset';
import useLanguageChange from './hooks/useLanguageChange';
import Logs from './Logs/Logs';

const AppLayout = (props) => {
    const isTncPopupVisible = useSelector(makeSelectIsTncPopupVisible());
    const {component: Component, path, isFullScreen} = props;
    const isTouchSupported = userDeviceService.isTouchSupported();
    const headerFooterClassName = cn({'lg-max:hidden': isFullScreen});
    const direction = useDirection();

    useLanguageChange();
    useIotDeviceConfigReset();
    useHistoryScrollTop();
    useLayoutChange();
    useListenBluetoothAvailabilityChange();
    useFwuBackgroundCheck();
    useDeviceIdlePingSchedulerPause();
    useDeviceConnectionNotification();
    useHideReconnectionGuideModal();
    useDocumentDescription();

    return (
        <Route
            path={path}
            render={(matchProps) => (
                <div
                    dir={direction}
                    className={cn('relative flex min-h-full flex-col items-center bg-romance', {
                        touch: isTouchSupported,
                    })}
                >
                    <AppHeader className={headerFooterClassName} />
                    <Accessibility />
                    <Notifications />
                    {isTncPopupVisible && <TermsOfUsePopup matchProps={matchProps} />}
                    <div className={cn('w-full flex-1', domClassNames.COMPONENTS_CONTAINER_CLASS_NAME)}>
                        <div className='page-content flex justify-center'>
                            <Component {...matchProps} />
                        </div>
                    </div>
                    <ModalsRoot />
                    <LoaderGlobal />
                    {(appConfig.getIsLogsVisible() || appConfig.getIsAppVersionVisible()) && <Logs />}
                    <OfflineDetector />
                    <YapPairingChecker />
                    <YapRequestStatusChecker />
                    <YapOfflineDetector />
                    <DataCollectionHandler />
                    <DeviceHolderConnectionChecker />
                    <DeviceConnectionChecker />
                    <AppFooter className={headerFooterClassName} />
                </div>
            )}
        />
    );
};

export default AppLayout;
